/**
 * 檔案用途區塊
 * @module PaymentPage
 * @purpose 處理錢包支付頁面的參數解析、鏈別/命名空間檢查、連線狀態引導與自動發送付款流程。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppKitAccount, useDisconnect, useAppKit, modal as appKitModal } from '@reown/appkit/react';
import { useChainId } from 'wagmi';
import { isAddress } from 'viem';
import PaymentHandler from '@modules/walletconnect/components/PaymentHandler';
import { useStrictSessionCheck } from '@modules/walletconnect/hooks/useStrictSessionCheck';
import { useWalletConnectI18n } from '@modules/walletconnect/hooks/useWalletConnectI18n';
import { useThirdPartyProvider } from '@shared/models';
import ContentLayout from '@shared/components/ContentLayout';
import {
  parseNamespace,
  getMappingByCurrencyAndNetwork,
} from '@modules/walletconnect/config/token-network-adapter-mapping';
import {
  type PaymentParams,
  isPaymentError,
  isValidWalletAddressForChain,
} from '@modules/walletconnect/helpers/payment-handlers';

/**
 * 宣告內容用途說明與單號關聯
 * @description PaymentPage 主元件，整合 URL 參數驗證、支付參數建構、網路/命名空間一致性檢查與交易結果導頁。
 * @purpose 作為 WalletConnect 支付流程的頁面層協調點，統一管理錯誤、取消重試與 UI 狀態切換。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
const PaymentPage: React.FC = () => {
  useStrictSessionCheck();

  const { payload } = useThirdPartyProvider();
  const { t } = useWalletConnectI18n(payload.lang);

  const [searchParams] = useSearchParams();
  const { address, isConnected, caipAddress } = useAppKitAccount();
  const { disconnect } = useDisconnect();
  const { open } = useAppKit();
  const currentChainId = useChainId();
  const [paymentParams, setPaymentParams] = useState<PaymentParams | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userCancelled, setUserCancelled] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const tokenParam = searchParams.get('tokens');
  const networkNameParam = searchParams.get('networkName');

  // 組裝 PaymentParams
  useEffect(() => {
    const addressFromUrl = searchParams.get('address');
    const amount = searchParams.get('amount');
    const redirectUrl = searchParams.get('redirectUrl');
    const isTokenParam = searchParams.get('isToken');
    const tokenAddress = searchParams.get('tokenAddress');

    if (!tokenParam || !networkNameParam || !addressFromUrl || !amount || !redirectUrl) {
      const missing: string[] = [];
      if (!tokenParam) {
        // 目前實際來源為 tokens，訊息以 tokens 表示，避免誤導
        missing.push('tokens');
      }
      if (!networkNameParam) {
        missing.push('networkName');
      }
      if (!addressFromUrl) {
        missing.push('address');
      }
      if (!amount) {
        missing.push('amount');
      }
      if (!redirectUrl) {
        missing.push('redirectUrl');
      }
      setError(`${t('payment.error.prefix')} ${t('payment.error.missingParams')}: ${missing.join(', ')}`);
      setPaymentParams(null);
      return;
    }

    const mapping = getMappingByCurrencyAndNetwork(tokenParam, networkNameParam);
    if (!mapping) {
      setError(
        `${t('payment.error.prefix')} ${t('payment.error.unsupportedCurrencyOrNetwork')}: tokens=${tokenParam}, networkName=${networkNameParam}`,
      );
      setPaymentParams(null);
      return;
    }

    if (!isValidWalletAddressForChain(addressFromUrl, mapping.chainId)) {
      setError(`${t('payment.error.prefix')} ${t('payment.error.invalidWalletAddress')}`);
      setPaymentParams(null);
      return;
    }

    const isToken = isTokenParam === 'true';
    if (isToken && !tokenAddress) {
      setError(`${t('payment.error.prefix')} ${t('payment.error.tokenAddressRequired')}`);
      setPaymentParams(null);
      return;
    }
    if (tokenAddress && !isAddress(tokenAddress)) {
      setError(`${t('payment.error.prefix')} ${t('payment.error.invalidContractAddress')}`);
      setPaymentParams(null);
      return;
    }

    setPaymentParams({
      walletAddress: addressFromUrl,
      amount,
      chainId: mapping.chainId,
      redirectUrl,
      token: mapping.token,
      isToken: isToken || mapping.isERC20,
      contractAddress: tokenAddress || undefined,
      decimals: mapping.decimals,
    });
    setError(null);
  }, [searchParams, tokenParam, networkNameParam, t]);

  const effectiveChainId = paymentParams?.chainId ?? null;
  const isWrongNetwork = useMemo(() => {
    if (!isConnected || !effectiveChainId || !currentChainId) {
      return false;
    }

    if (parseNamespace(effectiveChainId) !== 'eip155') {
      return false;
    }

    return currentChainId !== Number(effectiveChainId.split(':')[1]);
  }, [isConnected, effectiveChainId, currentChainId]);

  // 使用者在「Choose Network」中切換到目標網路後，關閉 modal 以顯示轉帳細節
  // 使用 modal 實例直接關閉
  useEffect(() => {
    if (!effectiveChainId || parseNamespace(effectiveChainId) !== 'eip155') {
      return;
    }
    const targetChainId = Number(effectiveChainId.split(':')[1]);
    if (currentChainId !== targetChainId) {
      return;
    }
    const closeModal = () => {
      void appKitModal?.close();
    };
    closeModal();
  }, [currentChainId, effectiveChainId]);

  const expectedNamespace = paymentParams ? parseNamespace(paymentParams.chainId) : null;
  const currentNamespace = caipAddress ? parseNamespace(caipAddress) : null;
  const namespaceMismatch = Boolean(
    isConnected && paymentParams && expectedNamespace && currentNamespace && expectedNamespace !== currentNamespace,
  );

  useEffect(() => {
    if (!namespaceMismatch) {
      return;
    }

    void disconnect();
  }, [namespaceMismatch, disconnect]);

  if (error) {
    return (
      <ContentLayout testId="PaymentPage">
        <div>{error}</div>
      </ContentLayout>
    );
  }

  // 載入中
  if (!paymentParams) {
    return (
      <ContentLayout testId="PaymentPage">
        <div>{t('payment.loading')}</div>
      </ContentLayout>
    );
  }

  // 未連接錢包
  if (!isConnected || !address || !caipAddress) {
    return (
      <ContentLayout testId="PaymentPage">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>{t('payment.connect.title')}</h3>
          <p style={{ marginTop: '16px', color: '#666' }}>{t('payment.connect.description')}</p>
          <button
            type="button"
            onClick={() => open({ view: 'Connect' })}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}>
            {t('payment.connect.button')}
          </button>
        </div>
      </ContentLayout>
    );
  }

  if (isWrongNetwork && effectiveChainId) {
    const numericTarget = Number(effectiveChainId.split(':')[1]);
    return (
      <ContentLayout testId="PaymentPage">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3 style={{ color: '#e6ad00' }}>{t('payment.networkMismatch.title')}</h3>
          <p style={{ marginTop: '16px', color: '#666' }}>
            {t('payment.networkMismatch.description')}
            <br />
            Chain ID: {currentChainId} → {numericTarget}
          </p>
          <button
            type="button"
            onClick={() => open({ view: 'Networks' })}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}>
            {t('payment.networkMismatch.button')}
          </button>
        </div>
      </ContentLayout>
    );
  }

  if (userCancelled) {
    return (
      <ContentLayout testId="PaymentPage">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ marginBottom: '16px' }}>{t('payment.status.userCancelled')}</p>
          <button
            type="button"
            onClick={() => {
              setUserCancelled(false);
              setRetryKey((k) => k + 1);
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}>
            {t('payment.retry')}
          </button>
        </div>
      </ContentLayout>
    );
  }

  const shortAddress = paymentParams.walletAddress.slice(0, 8) + '...' + paymentParams.walletAddress.slice(-6);

  return (
    <ContentLayout testId="PaymentPage">
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>{t('payment.summary.title')}</h3>
        <p style={{ marginTop: '12px', color: '#333' }}>
          {t('payment.summary.amount')}: {paymentParams.amount} {paymentParams.token ?? ''}
        </p>
        <p style={{ marginTop: '8px', color: '#333' }}>
          {t('payment.summary.address')}: {shortAddress}
        </p>
        <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>{t('payment.summary.autoSendHint')}</p>
      </div>
      <PaymentHandler
        key={retryKey}
        paymentParams={paymentParams}
        autoSend
        onSuccess={(txHash) => {
          const redirectParams = new URLSearchParams({ txHash, status: 'success' });
          window.location.href = `${paymentParams.redirectUrl}?${redirectParams.toString()}`;
        }}
        onError={(err) => {
          const message = isPaymentError(err)
            ? t(`payment.error.${err.code}` as import('@locale/keys').WalletConnectI18nKey)
            : err.message;
          setError(message);
        }}
        onUserCancel={() => {
          setUserCancelled(true);
        }}
      />
    </ContentLayout>
  );
};

export default PaymentPage;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:18:56.287Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Reformatted annotations into required three-section layout: added top file-purpose block, added middle declaration block above PaymentPage declaration with Jira external link, and appended bottom llm review record block. Kept runtime logic unchanged.
 */
